/**
 * Comments in the text: select text and click "Comment" (or Ctrl/Cmd+Alt+M); commented passages are
 * highlighted, and hovering one shows its comments (with Resolve).
 */
import { StateEffect, StateField, type Extension, type Range } from "@codemirror/state";
import { Decoration, EditorView, hoverTooltip, keymap, showTooltip, type DecorationSet, type Tooltip } from "@codemirror/view";

export interface CommentMark {
  id: string;
  quote: string;
  author: string;
  body: string;
  when: string;
}

export interface CommentHandlers {
  onComment(text: string): void;
  onResolve(id: string): void;
}

export const setCommentMarks = StateEffect.define<CommentMark[]>();

const marksField = StateField.define<{ list: CommentMark[]; deco: DecorationSet; ranges: { from: number; to: number; ids: string[] }[] }>({
  create: () => ({ list: [], deco: Decoration.none, ranges: [] }),
  update(value, tr) {
    let list = value.list;
    for (const e of tr.effects) if (e.is(setCommentMarks)) list = e.value;
    if (list === value.list && !tr.docChanged) return value;
    const text = tr.state.doc.toString();
    const ranges: { from: number; to: number; ids: string[] }[] = [];
    for (const c of list) {
      const q = c.quote.trim();
      if (q.length < 2) continue;
      const at = text.indexOf(q);
      if (at < 0) continue; // the passage was changed: the comment stays in the list only
      const same = ranges.find((r) => r.from === at && r.to === at + q.length);
      if (same) same.ids.push(c.id);
      else ranges.push({ from: at, to: at + q.length, ids: [c.id] });
    }
    const deco: Range<Decoration>[] = ranges.map((r) => Decoration.mark({ class: "cm-comment-mark", attributes: { "data-comments": String(r.ids.length) } }).range(r.from, r.to));
    return { list, deco: Decoration.set(deco, true), ranges };
  },
  provide: (f) => EditorView.decorations.from(f, (v) => v.deco),
});

function selectionTooltip(handlers: { current: CommentHandlers | null | undefined }) {
  return StateField.define<readonly Tooltip[]>({
    create: () => [],
    update(_tips, tr) {
      const sel = tr.state.selection.main;
      if (sel.empty || !tr.state.facet(EditorView.editable) || !handlers.current) return [];
      return [
        {
          pos: sel.head,
          above: sel.head === sel.from,
          strictSide: true,
          arrow: false,
          create: (view) => {
            const dom = document.createElement("div");
            dom.className = "cm-comment-bubble";
            const b = document.createElement("button");
            b.type = "button";
            b.textContent = "💬 Comment";
            b.title = "Comment on the selected text (Ctrl/Cmd+Alt+M) — visible to the members of this notebook or project";
            b.addEventListener("mousedown", (e) => {
              e.preventDefault();
              const r = view.state.selection.main;
              handlers.current?.onComment(view.state.sliceDoc(r.from, r.to));
            });
            dom.append(b);
            return { dom };
          },
        },
      ];
    },
    provide: (f) => showTooltip.computeN([f], (state) => state.field(f)),
  });
}

function commentHover(handlers: { current: CommentHandlers | null | undefined }) {
  return hoverTooltip((view, pos) => {
    const { ranges, list } = view.state.field(marksField);
    const hit = ranges.find((r) => pos >= r.from && pos <= r.to);
    if (!hit) return null;
    return {
      pos: hit.from,
      end: hit.to,
      above: true,
      create: () => {
        const dom = document.createElement("div");
        dom.className = "cm-comment-pop";
        for (const id of hit.ids) {
          const c = list.find((x) => x.id === id);
          if (!c) continue;
          const item = document.createElement("div");
          item.className = "cm-comment-item";
          const head = document.createElement("p");
          head.className = "cm-comment-head";
          head.textContent = `${c.author} · ${c.when}`;
          const body = document.createElement("p");
          body.className = "cm-comment-body";
          body.textContent = c.body;
          const resolve = document.createElement("button");
          resolve.type = "button";
          resolve.textContent = "Resolve";
          resolve.addEventListener("mousedown", (e) => {
            e.preventDefault();
            handlers.current?.onResolve(c.id);
          });
          item.append(head, body, resolve);
          dom.append(item);
        }
        return { dom };
      },
    };
  });
}

export function commentMarks(handlers: { current: CommentHandlers | null | undefined }): Extension {
  return [
    marksField,
    selectionTooltip(handlers),
    commentHover(handlers),
    keymap.of([
      {
        key: "Mod-Alt-m",
        run: (view) => {
          const r = view.state.selection.main;
          if (r.empty || !handlers.current) return false;
          handlers.current.onComment(view.state.sliceDoc(r.from, r.to));
          return true;
        },
      },
    ]),
  ];
}
